// Pure maths for the Payroll Financing tab — no Firestore/React imports.
//
// Bank formula (verified against every row of the bank's "FTM September"
// invoice): the bank quotes 1.9% a MONTH, annualised as 1.9% x 12 = 22.8%, and
// charges per calendar day on an actual/365 basis:
//   per-day markup = amount x monthlyRate x 12 / 365
//   markup         = per-day markup x days, rounded to PKR 0.1
//   days           = repayment date - withdrawal date (the withdrawal day counts,
//                    the repayment day does not: 30 Sep -> 9 Oct = 9 days)
// Interest is simple (never compounded) and runs only on principal still
// outstanding; a repaid slice carries its own interest to its repayment date.

export const PF_PORTALS = Object.freeze([
  { code: "ALM", label: "ALM Portal" },
  { code: "CBA", label: "CBA Portal" },
  { code: "DULA", label: "DULA Portal" }
]);

export const DEFAULT_MONTHLY_RATE = 0.019;
export const MOVEMENT_TYPES = Object.freeze({ TO_ALM: "TO_ALM", CHARGE: "CHARGE" });

export const round1 = (value) => Math.round(Number(value || 0) * 10) / 10;
export const round2 = (value) => Math.round(Number(value || 0) * 100) / 100;

export const isActive = (row) => row.entryStatus !== "REJECTED";

export function portalLabel(code) {
  return PF_PORTALS.find((portal) => portal.code === code)?.label || code;
}

export function daysBetween(from, to) {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.round((end - start) / 86400000);
}

export function perDayMarkup(amount, monthlyRate = DEFAULT_MONTHLY_RATE) {
  return (Number(amount || 0) * Number(monthlyRate) * 12) / 365;
}

export function markup(amount, days, monthlyRate = DEFAULT_MONTHLY_RATE) {
  if (!(days > 0) || !(amount > 0)) return 0;
  return round1(perDayMarkup(amount, monthlyRate) * days);
}

const sum = (rows, pick) => rows.reduce((total, row) => total + Number(pick(row) || 0), 0);

// The position of one withdrawal after every active movement and repayment.
export function withdrawalPosition({ withdrawal, movements, repayments, asOf }) {
  const id = withdrawal.id;
  const allocations = (list, extra) => list
    .filter(isActive)
    .flatMap((entry) => (entry.allocations || []).filter((item) => item.withdrawalId === id).map((item) => ({ ...item, ...extra(entry) })));

  const returned = sum(allocations(movements.filter((m) => m.type === MOVEMENT_TYPES.TO_ALM), () => ({})), (a) => a.amount);
  const charges = sum(allocations(movements.filter((m) => m.type === MOVEMENT_TYPES.CHARGE), () => ({})), (a) => a.amount);
  const repaymentSlices = allocations(repayments, () => ({}));
  const repaidPrincipal = sum(repaymentSlices, (a) => a.principal);
  const interestPaid = sum(repaymentSlices, (a) => a.interest);

  const principal = Number(withdrawal.amount || 0);
  const outstanding = round2(principal - repaidPrincipal);
  const withEmployee = round2(principal - returned - charges);
  const days = Math.max(0, daysBetween(withdrawal.date, asOf));
  const accruedInterest = markup(outstanding, days, withdrawal.monthlyRate ?? DEFAULT_MONTHLY_RATE);

  let reconStatus = "COMPLETE";
  if (withEmployee < 0) reconStatus = "OVER";
  else if (returned === 0 && charges === 0) reconStatus = "NOT_RETURNED";
  else if (withEmployee > 0) reconStatus = "PARTIAL";

  return {
    withdrawal, principal, returned: round2(returned), charges: round2(charges), withEmployee,
    repaidPrincipal: round2(repaidPrincipal), interestPaid: round1(interestPaid),
    outstanding, days, accruedInterest, payableNow: round1(outstanding + accruedInterest),
    reconStatus,
    repaid: outstanding <= 0
  };
}

export const RECON_LABELS = Object.freeze({
  COMPLETE: "Complete",
  NOT_RETURNED: "Money still with employee",
  PARTIAL: "Part returned",
  OVER: "Over-returned — check"
});

// Everything for the (optionally portal-filtered) book as of a date.
export function buildPosition({ withdrawals, movements, repayments, uses = [], asOf, portal = "ALL" }) {
  const inScope = (row) => portal === "ALL" || row.portal === portal;
  const w = withdrawals.filter((row) => isActive(row) && inScope(row));
  const m = movements.filter((row) => isActive(row) && inScope(row));
  const r = repayments.filter((row) => isActive(row) && inScope(row));
  const rows = w
    .map((withdrawal) => withdrawalPosition({ withdrawal, movements: m, repayments: r, asOf }))
    .sort((a, b) => `${a.withdrawal.date} ${a.withdrawal.time || ""}`.localeCompare(`${b.withdrawal.date} ${b.withdrawal.time || ""}`));

  const totals = {
    withdrawn: round2(sum(rows, (x) => x.principal)),
    returned: round2(sum(rows, (x) => x.returned)),
    charges: round2(sum(rows, (x) => x.charges)),
    withEmployee: round2(sum(rows, (x) => x.withEmployee)),
    repaidPrincipal: round2(sum(rows, (x) => x.repaidPrincipal)),
    interestPaid: round1(sum(rows, (x) => x.interestPaid)),
    outstanding: round2(sum(rows, (x) => x.outstanding)),
    accruedInterest: round1(sum(rows, (x) => x.accruedInterest))
  };
  totals.payableNow = round1(totals.outstanding + totals.accruedInterest);

  const activeUses = uses.filter((row) => isActive(row) && (portal === "ALL" || !row.portal || row.portal === portal));
  const funds = buildFundsOnHand({ movements: m, uses: activeUses });
  return { rows, totals, funds };
}

// Funds received at the company's accounts less what was spent — by account.
export function buildFundsOnHand({ movements, uses }) {
  const byAccount = new Map();
  const bucket = (name) => {
    const key = String(name || "Unspecified account").trim() || "Unspecified account";
    if (!byAccount.has(key)) byAccount.set(key, { account: key, received: 0, used: 0 });
    return byAccount.get(key);
  };
  movements.filter((m) => isActive(m) && m.type === MOVEMENT_TYPES.TO_ALM).forEach((m) => { bucket(m.companyAccount).received += Number(m.amount || 0); });
  uses.filter(isActive).forEach((u) => { bucket(u.companyAccount).used += Number(u.amount || 0); });
  const accounts = [...byAccount.values()].map((a) => ({ ...a, received: round2(a.received), used: round2(a.used), onHand: round2(a.received - a.used) }));
  const received = round2(sum(accounts, (a) => a.received));
  const used = round2(sum(accounts, (a) => a.used));
  return { accounts, received, used, onHand: round2(received - used) };
}

// Per-employee view: what each conduit account received, passed back, lost in charges.
export function buildEmployeeRecon(rows) {
  const byEmployee = new Map();
  rows.forEach((row) => {
    const key = row.withdrawal.employeeId || row.withdrawal.employeeName;
    if (!byEmployee.has(key)) byEmployee.set(key, { employeeId: row.withdrawal.employeeId, name: row.withdrawal.employeeName, bank: row.withdrawal.employeeBank, account: row.withdrawal.employeeAccount, withdrawn: 0, returned: 0, charges: 0, withEmployee: 0 });
    const entry = byEmployee.get(key);
    entry.withdrawn += row.principal; entry.returned += row.returned; entry.charges += row.charges; entry.withEmployee += row.withEmployee;
  });
  return [...byEmployee.values()].map((e) => ({ ...e, withdrawn: round2(e.withdrawn), returned: round2(e.returned), charges: round2(e.charges), withEmployee: round2(e.withEmployee) }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

// Splits an amount across items oldest-first. items: [{ id, open }].
export function allocateFifo(items, amount) {
  let remaining = round2(amount);
  const allocations = [];
  for (const item of items) {
    if (remaining <= 0) break;
    const open = round2(item.open);
    if (!(open > 0)) continue;
    const take = Math.min(open, remaining);
    allocations.push({ withdrawalId: item.id, amount: round2(take) });
    remaining = round2(remaining - take);
  }
  return { allocations, unallocated: remaining };
}

// Default repayment lines for "repay these in full on this date".
export function suggestRepayment({ positions, repayDate }) {
  return positions.filter((p) => p.outstanding > 0).map((p) => {
    const days = Math.max(0, daysBetween(p.withdrawal.date, repayDate));
    return {
      withdrawalId: p.withdrawal.id,
      principal: p.outstanding,
      days,
      interest: markup(p.outstanding, days, p.withdrawal.monthlyRate ?? DEFAULT_MONTHLY_RATE)
    };
  });
}

export function validateRepayment({ allocations, positions, repayDate }) {
  if (!repayDate) return "Enter the repayment date.";
  const lines = allocations.filter((a) => Number(a.principal) > 0 || Number(a.interest) > 0);
  if (!lines.length) return "Select at least one withdrawal to repay.";
  for (const line of lines) {
    const position = positions.find((p) => p.withdrawal.id === line.withdrawalId);
    if (!position) return "A selected withdrawal no longer exists.";
    if (repayDate < position.withdrawal.date) return `${position.withdrawal.employeeName} ${position.withdrawal.date}: repayment is before the withdrawal date.`;
    if (Number(line.principal) < 0 || Number(line.interest) < 0) return "Amounts cannot be negative.";
    if (round2(line.principal) > position.outstanding) return `${position.withdrawal.employeeName} ${position.withdrawal.date}: principal exceeds the ${position.outstanding} outstanding.`;
  }
  return "";
}

export function validateMovementAllocations({ amount, allocations, positions, type }) {
  const total = round2(sum(allocations, (a) => a.amount));
  if (!(round2(amount) > 0)) return "Enter an amount greater than zero.";
  if (!allocations.length) return "This amount is not allocated to any withdrawal.";
  if (total !== round2(amount)) return `Allocated ${total} but the amount is ${round2(amount)}.`;
  for (const line of allocations) {
    const position = positions.find((p) => p.withdrawal.id === line.withdrawalId);
    if (!position) return "A selected withdrawal no longer exists.";
    // Over-return is allowed to be recorded (and flagged) only for charges that
    // can exceed nothing — for returns the open amount is a hard ceiling.
    if (type === MOVEMENT_TYPES.TO_ALM && round2(line.amount) > position.withEmployee) {
      return `${position.withdrawal.employeeName} ${position.withdrawal.date}: only ${position.withEmployee} is still with the employee.`;
    }
    if (type === MOVEMENT_TYPES.CHARGE && round2(line.amount) > position.withEmployee) {
      return `${position.withdrawal.employeeName} ${position.withdrawal.date}: a charge of ${line.amount} exceeds the ${position.withEmployee} still with the employee.`;
    }
  }
  return "";
}

// Compare the bank's own invoice figures (from an uploaded sheet) with ours.
export function compareBankInvoice(withdrawal) {
  const bank = withdrawal.bankInvoice;
  if (!bank) return null;
  const days = daysBetween(withdrawal.date, bank.repaymentDate);
  const ours = markup(withdrawal.amount, days, withdrawal.monthlyRate ?? DEFAULT_MONTHLY_RATE);
  return {
    repaymentDate: bank.repaymentDate, days, bankDays: bank.days,
    bankMarkup: round1(bank.markup), ourMarkup: ours, difference: round1(bank.markup - ours),
    bankTotal: round1(bank.total), match: round1(bank.markup) === ours && days === Number(bank.days)
  };
}
